/* ═══════════════════════════════════════════════════════════════════════════
   💾 composeDraftWeb.js — the one Vibe draft this browser keeps per account.
   (The phone's twin is app/services/composeDraftStore.ts.)

   "Save as draft" on the composer's ✕ writes it; opening an empty composer
   brings it back. What it may hold is services/postCompose cleanDraft.

   🟢 THE PICTURES GO TO IndexedDB, the words to localStorage. A File cannot
      be written to localStorage, but IndexedDB stores it whole (structured
      clone) — with the editor's record for each one beside it. A browser with
      no IndexedDB (some private windows) keeps the words and loses only the
      files, which the restored draft then simply does not have.
   ═══════════════════════════════════════════════════════════════════════════ */
import { cleanDraft, draftWorthKeeping } from './postCompose';

const LS = (uid) => `allo_vibe_draft_v1:${uid}`;
const DB = 'allo_vibe_drafts';
const STORE = 'files';
const subs = new Set();
const emit = () => subs.forEach((f) => { try { f(); } catch (_) { /* listener */ } });
const mem = new Map();

export function subscribeDraft(fn) { subs.add(fn); return () => subs.delete(fn); }
export function peekDraft(uid) { return uid ? (mem.get(uid) || null) : null; }

function idb() {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => { try { r.result.createObjectStore(STORE); } catch (_) { /* exists */ } };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
    } catch (_) { resolve(null); }
  });
}
async function idbDo(mode, fn) {
  const db = await idb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? req.result : true);
      tx.onerror = () => resolve(null);
    } catch (_) { resolve(null); }
  });
}

const kindOf = (f) => (String((f && f.type) || '').startsWith('video/') ? 'video' : 'image');

/** { draft, files, edits } or null. */
export async function loadDraft(uid) {
  if (!uid) return null;
  let raw = null;
  try { raw = JSON.parse(window.localStorage.getItem(LS(uid)) || 'null'); } catch (_) { raw = null; }
  let files = [];
  let edits = [];
  if (raw && raw.fileCount > 0) {
    const got = await idbDo('readonly', (s) => s.get(uid));
    if (got && Array.isArray(got.files)) { files = got.files.filter((f) => f instanceof Blob); edits = Array.isArray(got.edits) ? got.edits : []; }
  }
  const draft = raw ? cleanDraft({ ...raw, media: files.map((f) => ({ uri: 'idb:file', type: kindOf(f) })) }, Date.now()) : null;
  const out = draft ? { draft, files, edits: edits.slice(0, files.length) } : null;
  mem.set(uid, out ? out.draft : null);
  emit();
  return out;
}

export async function saveDraft(uid, draft, files = [], edits = []) {
  if (!uid) return false;
  const media = files.map((f) => ({ uri: 'idb:file', type: kindOf(f) }));
  const clean = cleanDraft({ ...draft, media, v: 1, at: Date.now() }, Date.now());
  if (!clean || !draftWorthKeeping(clean)) { await clearDraft(uid); return false; }
  const { media: _m, ...rest } = clean;
  try { window.localStorage.setItem(LS(uid), JSON.stringify({ ...rest, fileCount: files.length })); } catch (_) { return false; }
  if (files.length) await idbDo('readwrite', (s) => s.put({ files, edits }, uid));
  else await idbDo('readwrite', (s) => s.delete(uid));
  mem.set(uid, clean);
  emit();
  return true;
}

export async function clearDraft(uid) {
  if (!uid) return;
  try { window.localStorage.removeItem(LS(uid)); } catch (_) { /* nothing */ }
  await idbDo('readwrite', (s) => s.delete(uid));
  mem.set(uid, null);
  emit();
}
